import React, { useState, useMemo } from 'react';
import { SortField, SortOrder } from '../../types/Project';
import { Input } from '../atoms/Input';
import { Select } from '../atoms/Select';
import { ProjectList } from '../organisms/ProjectList';
import {
  getUniqueBrands,
  filterProjectsByBrand,
  filterProjectsBySearch,
} from '../../utils/projectUtils';
import { useProjects } from '../../hooks/useProjects';

export const ProjectsPage: React.FC = () => {
  const { projects, loading, error } = useProjects();
  const [sortField, setSortField] = useState<SortField>('startDate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [brandFilter, setBrandFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const availableBrands = useMemo(() => getUniqueBrands(projects), [projects]);

  const filteredProjects = useMemo(() => {
    let filtered = filterProjectsByBrand(projects, brandFilter);
    filtered = filterProjectsBySearch(filtered, searchQuery);
    return filtered;
  }, [projects, brandFilter, searchQuery]);

  const brandOptions = [
    { value: 'all', label: 'Todas as marcas' },
    ...availableBrands.map((brand) => ({ value: brand, label: brand })),
  ];

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  if (error) {
    return (
      <div className="p-6 sm:p-10">
        <div className="text-center p-8 bg-white/80 dark:bg-dark-300/80 backdrop-blur-sm rounded-3xl shadow-soft border-0 max-w-md mx-auto">
          <div className="text-red-600 dark:text-red-400 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-soft-black dark:text-gray-100 mb-2">
            Erro ao carregar projetos
          </h2>
          <p className="text-soft-gray dark:text-gray-400 mb-4">
            {error.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-soft-black hover:bg-soft-gray text-white font-medium rounded-2xl transition-all duration-300 shadow-soft hover:shadow-soft-lg"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-10">
      <header className="mb-6 sm:mb-10">
        <h1 className="text-2xl sm:text-4xl font-bold text-soft-black dark:text-gray-100 mb-1 sm:mb-2">
          Projetos
        </h1>
        <p className="text-sm sm:text-base text-soft-gray dark:text-gray-400">
          Gerenciamento de projetos de influenciadores
        </p>
      </header>

      {loading && projects.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-warm-500 dark:border-dark-purple-300 mb-4"></div>
            <p className="text-soft-gray dark:text-gray-400 font-medium">Carregando projetos...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white/80 dark:bg-dark-300/80 backdrop-blur-sm rounded-3xl shadow-soft border-0 p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col lg:flex-row gap-4 lg:items-end">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Pesquisar
                </label>
                <Input
                  type="text"
                  placeholder="Buscar por título..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  icon={
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  }
                />
              </div>
              <div className="w-full lg:w-64">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Marcas
                </label>
                <Select
                  options={brandOptions}
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="mb-4 sm:mb-5">
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
              Exibindo {filteredProjects.length} projeto
              {filteredProjects.length !== 1 ? 's' : ''}
            </p>
          </div>

          <ProjectList
            projects={filteredProjects}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        </>
      )}
    </div>
  );
};
